import React, { useState, useEffect, useRef } from "react";

import "../styles/TutorBoard.css";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import Footer from "../Footer";

import styled from "styled-components";
import {
  BadgeCheck,
  UserPlus,
  Activity,
  Users,
  Building,
  Sparkles,
  MapPinCheck,
  UserRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

import {
  Mars,
  MapPin,
  Clock,
  ExternalLink,
  Search,
  Filter,
  X,
  Phone,
  Star,
  GraduationCap,
  DollarSign,
  ChevronDown,
} from "lucide-react";

// ---- Loading Skeletons ----
const TNSkeletonList = React.memo(function TNSkeletonList({ count = 6 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div className="tutor-card tn-skeleton-card" key={`sk-${i}`}>
          <div className="tutor-card__content">
            <div className="tutor-image">
              <div className="tn-skeleton tn-skeleton-avatar" />
            </div>
            <div className="tutor-info">
              <div
                className="tn-skeleton tn-skeleton-line"
                style={{ width: "45%", height: 20, marginBottom: 8 }}
              />
              <div
                className="tn-skeleton tn-skeleton-line"
                style={{ width: "70%", height: 14, marginBottom: 6 }}
              />
              <div
                className="tn-skeleton tn-skeleton-line"
                style={{ width: "85%", height: 14, marginBottom: 12 }}
              />

              <div className="tn-skeleton-chip-row">
                <span className="tn-skeleton tn-skeleton-chip" />
                <span className="tn-skeleton tn-skeleton-chip" />
                <span className="tn-skeleton tn-skeleton-chip" />
              </div>

              <div
                className="tn-skeleton tn-skeleton-line"
                style={{ width: "100%", height: 12, marginTop: 10 }}
              />
            </div>
          </div>

          <div className="tn-skeleton tn-skeleton-button" />
        </div>
      ))}
    </>
  );
});

// ---- Counter Section (tnc-) ----
const TNCounterSection = React.memo(function TNCounterSection() {
  const formatBn = (n) => {
    try {
      return new Intl.NumberFormat("bn-BD").format(n);
    } catch {
      const map = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];
      return String(n).replace(/\d/g, (d) => map[d]);
    }
  };

  const targets = React.useMemo(() => {
    const r = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
    return {
      liveTutors: r(600, 1000),
      positions: r(600, 1000),
      institutes: 64,
      newTutors: r(54, 100),
    };
  }, []);

  const useCountUp = (to, duration = 1800) => {
    const [val, setVal] = React.useState(0);
    React.useEffect(() => {
      let raf = 0,
        start = 0;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min(1, (ts - start) / duration);
        setVal(Math.floor(p * to));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
      return () => cancelAnimationFrame(raf);
    }, [to, duration]);
    return val;
  };

  const v1 = useCountUp(targets.liveTutors);
  const v2 = useCountUp(targets.positions);
  const v3 = useCountUp(targets.institutes);
  const v4 = useCountUp(targets.newTutors);

  const items = [
    { icon: Activity, label: "লাইভ টিউটর", value: v1 },
    { icon: Users, label: "লাইভ স্টুডেন্ট", value: v2, plus: true },
    { icon: Building, label: "নতুন টিউটর", value: v3 },
    { icon: Sparkles, label: "ভেরিফাইড টিউটর", value: v4 },
  ];

  return (
    <section className="tnc-section" aria-label="site counters">
      <div className="tnc-wrap">
        {items.map(({ icon: Icon, label, value, plus }, i) => (
          <div className="tnc-card" key={i}>
            <div className="tnc-iconRing">
              <span className="tnc-icon">
                <Icon size={22} aria-hidden="true" />
              </span>
              <span className="tnc-pulse" aria-hidden="true" />
            </div>
            <div className="tnc-texts baloo-da-2-tutor">
              <div className="tnc-label">{label}</div>
              <div className="tnc-value">
                {formatBn(value)}
                {plus && value >= targets.positions ? "+" : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
});

const TutorNetwork = () => {
  const [tutors, setTutors] = useState([]);
  // --- Fair rotation: randomize full list per refresh ---
  const RANDOMIZE_ALL = false; // set false to revert to server paging
  const [allTutors, setAllTutors] = useState(null);
  const randomSeedRef = useRef(Date.now()); // new seed each hard refresh
  const randomizedOnce = useRef(false); // avoid re-shuffling on page change

  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  const mapToOptions = (items, labelKey = "name") =>
    items.map((item) => ({ value: item.id, label: item[labelKey] }));

  // ---- Random helpers (seeded shuffle so one refresh = one order) ----
  const seededRandom = (seed) => {
    let t = seed + 0x6d2b79f5;
    return () => {
      t += 0x6d2b79f5;
      let x = Math.imul(t ^ (t >>> 15), 1 | t);
      x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
      return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
    };
  };
  const shuffleWithSeed = (arr, seed) => {
    const rand = seededRandom(seed);
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // BIO Sanitization
  const sanitizeBio = (bio = "") => {
    return bio.replace(
      /(\+?88)?\s?0?(1[1-9]\d{8,9})|\b\d{11,14}\b/g,
      "[ Contact Hidden ]",
    );
  };

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTutors, setTotalTutors] = useState(0);
  const tutorsPerPage = 10;
  const abortRef = useRef(null);
  const searchDebounceRef = useRef(null);

  // Filter states - matching backend API parameters
  const [filters, setFilters] = useState({
    medium_id: "",
    level_id: "",
    subject_id: "",
    division_id: "",
    district_id: "",
    area_id: "",
    mode: "",
    availability: "",
    gender: "",
    search: "",
  });

  // Filter options for dropdowns
  const [filterOptions, setFilterOptions] = useState({
    mediums: [],
    levels: [],
    subjects: [],
    divisions: [],
    districts: [],
    areas: [],
    availabilities: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
  });

  // Pagination
  const PAGE_WINDOW = 5;
  const PAGE_STEP = 2;
  const [windowStart, setWindowStart] = useState(1);

  useEffect(() => {
    const tp = Number(totalPages) || 1;
    const cp = Number(currentPage) || 1;
    const maxStart = Math.max(1, tp - PAGE_WINDOW + 1);
    const desiredStart = Math.max(
      1,
      Math.min(cp - Math.floor(PAGE_WINDOW / 2), maxStart),
    );
    setWindowStart(desiredStart);
  }, [currentPage, totalPages]);

  const visiblePages = React.useMemo(() => {
    const start = Number(windowStart);
    const end = Math.min(start + PAGE_WINDOW - 1, Number(totalPages));
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [windowStart, totalPages]);

  const goPrev = () => {
    if (Number(currentPage) <= 1) return;
    const nextPage = Number(currentPage) - 1;
    handlePageChange(nextPage);
    if (nextPage < windowStart) {
      setWindowStart((s) => Math.max(1, s - PAGE_STEP));
    }
  };

  // SEO
  const location = useLocation();
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://tn.goatteachers.com";

  const canonicalUrl = React.useMemo(() => {
    const url = new URL(origin + (location?.pathname || "/"));
    const params = new URLSearchParams();

    if (filters.search) params.set("q", filters.search);
    if (filters.division_id) params.set("division_id", filters.division_id);
    if (filters.district_id) params.set("district_id", filters.district_id);
    if (filters.area_id) params.set("area_id", filters.area_id);
    if (filters.subject_id) params.set("subject_id", filters.subject_id);
    if (filters.level_id) params.set("level_id", filters.level_id);
    if (filters.medium_id) params.set("medium_id", filters.medium_id);
    if (Number(currentPage) > 1) params.set("page", String(currentPage));

    const qs = params.toString();
    url.search = qs ? `?${qs}` : "";
    return url.toString();
  }, [origin, location?.pathname, filters, currentPage]);

  const baseTitle = "Tutor Network — বাংলাদেশের প্রথম ফ্রি টিউটর প্ল্যাটফর্ম";
  const shortContext = [
    filters.search && `“${filters.search}”`,
    filters.subject_id && "Subject",
    filters.level_id && "Level",
    filters.medium_id && "Medium",
    filters.area_id && "Area",
  ]
    .filter(Boolean)
    .join(" · ");

  const pageTitle = shortContext
    ? `${shortContext} — Tutors | Goatt`
    : baseTitle;
  const pageDesc = tutors.length
    ? `ভেরিফাইড টিউটর তালিকা — বিষয়, লেভেল, মাধ্যম ও লোকেশন দিয়ে ফিল্টার করুন। মোট ${
        totalTutors || tutors.length
      } ফলাফল।`
    : `বাংলাদেশে প্রাইভেট টিউটর খুঁজুন — বিষয়, লেভেল, মাধ্যম ও লোকেশন দিয়ে সার্চ করুন।`;

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
      {
        "@type": "ListItem",
        position: 2,
        name: "Tutor Network",
        item: origin + (location?.pathname || "/"),
      },
    ],
  };

  const itemListLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: tutors.slice(0, 10).map((t, i) => {
      const id = t.id || t._id || i;
      const name = t.name || "Tutor";
      return {
        "@type": "ListItem",
        position: i + 1,
        url: `${origin}/tutor/${id}`,
        name: name,
      };
    }),
  };

  const goNext = () => {
    if (Number(currentPage) >= Number(totalPages)) return;
    const nextPage = Number(currentPage) + 1;
    handlePageChange(nextPage);
    const windowEnd = windowStart + PAGE_WINDOW - 1;
    if (nextPage > windowEnd) {
      const maxStart = Math.max(1, Number(totalPages) - PAGE_WINDOW + 1);
      setWindowStart((s) => Math.min(s + PAGE_STEP, maxStart));
    }
  };

  // Memory Store the choice in localStorage so they aren't asked again:
  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (role) setUserRole(role);
  }, []);

  const handleRoleSelect = (role) => {
    setUserRole(role);
    localStorage.setItem("userRole", role);
    if (role === "tutor") window.location.href = "/tutor-submission";
  };

  // Fetch filter options (you might want to create separate endpoints for these)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const urls = [
          "https://tn.server.goatteachers.com/api/mediums",
          "https://tn.server.goatteachers.com/api/levels",
          "https://tn.server.goatteachers.com/api/subjects",
          "https://tn.server.goatteachers.com/api/divisions",
          "https://tn.server.goatteachers.com/api/districts",
          "https://tn.server.goatteachers.com/api/areas",
        ];

        const responses = await Promise.all(urls.map((url) => fetch(url)));

        responses.forEach((res, i) => {
          if (!res.ok) {
            throw new Error(
              `Failed to fetch ${urls[i]} - status ${res.status}`,
            );
          }
        });

        const [mediums, levels, subjects, divisions, districts, areas] =
          await Promise.all(responses.map((res) => res.json()));

        setFilterOptions((prev) => ({
          ...prev,
          mediums,
          levels,
          subjects,
          divisions,
          districts,
          areas,
          availabilities: [
            "Saturday",
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
          ],
        }));
      } catch (error) {
        console.error("❌ Failed to fetch filter options:", error);
      }
    };

    fetchFilterOptions();
  }, []);

  // Build query parameters for API call (NORMAL - no verified forcing)
  const buildQueryParams = (page = currentPage, f = filters) => {
    const params = new URLSearchParams();

    Object.entries(f || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value);
      }
    });

    // ✅ DO NOT force verified
    // params.set("is_verified", "1");

    params.set("page", String(page));
    params.set("limit", String(tutorsPerPage));

    return params.toString();
  };

  const isVerified = (v) =>
    v === true ||
    v === 1 ||
    v === "1" ||
    v === "true" ||
    v === "TRUE" ||
    v === "yes" ||
    v === "YES";

  // Fetch tutors from backend
  const fetchTutors = async ({ page = currentPage, f = filters } = {}) => {
    //  cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);

      const queryParams = buildQueryParams(page, f);
      const response = await fetch(
        `https://tn.server.goatteachers.com/api/tutors?${queryParams}`,
        {
          signal: controller.signal,
        },
      );

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load tutors");
      }

      // normalize
      const total = Number(data.total ?? data.count ?? 0);
      const pageFromServer = Number(data.page ?? data.currentPage ?? page ?? 1);
      const limit = Number(data.limit ?? data.perPage ?? tutorsPerPage);
      const totalPagesFromServer =
        data.totalPages != null
          ? Math.max(1, Number(data.totalPages))
          : Math.max(1, Math.ceil(total / limit));

      const rows = data.tutors ?? data.data ?? [];

      //  IMPORTANT:
      // backend must already return VERIFIED tutors only (is_verified = true)
      // so do NOT filter/slice here
      setTutors(rows);
      setTotalTutors(total);
      setTotalPages(totalPagesFromServer);
      setCurrentPage(pageFromServer);
    } catch (err) {
      //  ignore abort errors
      if (err?.name === "AbortError") return;
      console.error("Error fetching tutors:", err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch ALL pages, shuffle once, then do client-side pagination
  const fetchAllTutorsAndShuffle = async ({
    refreshSliceOnly = false,
  } = {}) => {
    try {
      setLoading(true);

      // reuse your existing builder
      const buildQuery = () =>
        new URLSearchParams(buildQueryParams()).toString();

      // local copy of your normalize (kept here to avoid moving your original)
      const normalizePaginationLocal = (d, fallbackLimit) => {
        const total = Number(d.total ?? d.count ?? 0);
        const page = Number(d.page ?? d.currentPage ?? 1);
        const limit = Number(d.limit ?? d.perPage ?? fallbackLimit ?? 10);
        const totalPages =
          d.totalPages != null
            ? Math.max(1, Number(d.totalPages))
            : Math.max(1, Math.ceil(total / limit));
        const tutors = d.tutors ?? d.data ?? [];
        return { tutors, total, page, limit, totalPages };
      };

      if (!refreshSliceOnly) {
        // 1) Probe first page (with current filters) to discover total pages
        const firstUrl = `https://tn.server.goatteachers.com/api/tutors?${buildQuery()}`;
        const firstRes = await fetch(firstUrl);
        const firstJson = await firstRes.json();
        if (!firstRes.ok || firstJson.success === false) {
          throw new Error(firstJson.message || "Failed to load tutors");
        }
        const norm = normalizePaginationLocal(firstJson, tutorsPerPage);
        const totalPagesServer = Number(norm.totalPages) || 1;

        // 2) Pull all pages
        let all = norm.tutors || [];
        if (totalPagesServer > 1) {
          const restPages = Array.from(
            { length: totalPagesServer - 1 },
            (_, i) => i + 2,
          );
          const results = await Promise.all(
            restPages.map(async (p) => {
              const qp = new URLSearchParams(buildQueryParams());
              qp.set("page", String(p));
              const res = await fetch(
                `https://tn.server.goatteachers.com/api/tutors?${qp.toString()}`,
              );
              const json = await res.json();
              return { ok: res.ok, json };
            }),
          );
          results.forEach(({ ok, json }) => {
            if (ok && json) {
              const chunk = json.tutors ?? json.data ?? [];
              all = all.concat(chunk);
            }
          });
        }

        // 3) Shuffle deterministically for this refresh
        const seed = randomSeedRef.current;
        const shuffled = shuffleWithSeed(all, seed);

        // NORMAL - no verified filtering
        const finalList = rankAndFilterTutors(shuffled);
        setAllTutors(finalList);

        const total = finalList.length;
        const totalPagesClient = Math.max(1, Math.ceil(total / tutorsPerPage));

        randomizedOnce.current = true;

        setTotalTutors(total);
        setTotalPages(totalPagesClient);

        // keep page safe
        const safePage = Math.min(
          Math.max(1, Number(currentPage)),
          totalPagesClient,
        );
        setCurrentPage(safePage);

        const start = (safePage - 1) * tutorsPerPage;
        const end = start + tutorsPerPage;
        setTutors(finalList.slice(start, end));
      } else {
        // Page changed: just slice again; no network hit
        if (Array.isArray(allTutors)) {
          const total = allTutors.length;
          const totalPagesClient = Math.max(
            1,
            Math.ceil(total / tutorsPerPage),
          );
          setTotalTutors(total);
          setTotalPages(totalPagesClient);

          const start = (Number(currentPage) - 1) * tutorsPerPage;
          const end = start + tutorsPerPage;
          setTutors(allTutors.slice(start, end));
        }
      }
    } catch (e) {
      console.error("Randomized fetch failed:", e.message);
    } finally {
      setLoading(false);
    }
  };

  // Rank and filter tutors (NORMAL - no verified filtering)
  const rankAndFilterTutors = (list) => {
    if (!Array.isArray(list)) return [];
    return list; // no filtering
  };

  // Fetch tutors when filters or page changes
  // When filters change: re-build the shuffled list (one network sweep)

  const prevFiltersKeyRef = useRef(JSON.stringify(filters));

  useEffect(() => {
    if (RANDOMIZE_ALL) return; // keep your random mode separate

    const key = JSON.stringify(filters);

    // If filters changed, force page 1 (and DO NOT fetch old page)
    if (prevFiltersKeyRef.current !== key) {
      prevFiltersKeyRef.current = key;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    // Now safe to fetch (page is correct)
    fetchTutors({ page: currentPage, f: filters });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage, RANDOMIZE_ALL]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      medium_id: "",
      level_id: "",
      subject_id: "",
      division_id: "",
      district_id: "",
      area_id: "",
      mode: "",
      availability: "",
      gender: "",
      search: "",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters =
    Object.values(filters).some((filter) => filter !== "") || searchTerm !== "";

  const formatTutorData = (tutor) => {
    return {
      id: tutor.id,
      name: tutor.name,
      is_verified: isVerified(tutor.is_verified),
      profile_picture: tutor.profile_picture,
      gender: tutor.gender,
      bio: tutor.bio,
      teaching_mode: tutor.teaching_mode,
      areas:
        tutor.areas?.map(
          (a) =>
            `${a.name}${a.district ? ", " + a.district : ""}${
              a.division ? " (" + a.division + ")" : ""
            }`,
        ) || [],
      location: [
        tutor.location?.area,
        tutor.location?.district,
        tutor.location?.division,
      ]
        .filter(Boolean)
        .join(", "),
      availability: tutor.availability || [],
      phone_number: tutor.connect?.phone || "",
      whatsapp_number: tutor.connect?.whatsapp || "",
      subjects: tutor.subjects || [],
      mediums: tutor.mediums || [],
      levels: tutor.levels || [],
      degree: tutor.degree || "—",
      institute: tutor.institute || "—",
      field_of_study: tutor.field_of_study || "—",
      start_year: tutor.study_period?.split("–")[0] || "",
      end_year: tutor.study_period?.split("–")[1] || "",
      slug: `tutor-${tutor.id}`,
      rating: tutor.rating || "4.8",
    };
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    const next = Math.max(1, Math.min(Number(page), Number(totalPages)));
    setCurrentPage((prev) => (prev === next ? prev : next));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="tutor-network">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />

        {!loading && tutors.length === 0 && (
          <meta name="robots" content="noindex,follow" />
        )}

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbLd)}
        </script>
        {tutors.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(itemListLd)}
          </script>
        )}
      </Helmet>

      <div className="tutor-network-container">
        <h1 className="text-center tutor-board-title">টিউটর বোর্ড</h1>

        {/* APPLICATION BUTTONS  */}
        <div className="application-btnss font-bold">
          <button
            className="tuition-apply-btn-N inline-flex items-center gap-2"
            onClick={() => (window.location.href = "/tuition-board")}
          >
            <i
              className="fa-solid fa-magnifying-glass text-white"
              style={{ fontSize: 10 }}
              aria-hidden="true"
            ></i>
            <span className="bg-gradient-to-r from-emerald-400 via-emerald-400 to-emerald-400 bg-clip-text text-transparent">
              Find Tuitions
            </span>
          </button>

          <button
            className="tuition-apply-btn-N inline-flex items-center gap-2"
            onClick={() => (window.location.href = "/tutor-board")}
          >
            <i
              className="fa-solid fa-user-graduate text-white"
              style={{ fontSize: 10 }}
              aria-hidden="true"
            ></i>
            <span className="bg-gradient-to-r from-emerald-400 via-emerald-400 to-emerald-400 bg-clip-text text-transparent">
              Hire Tutor
            </span>
          </button>

          <button
            className="tuition-apply-btn-N inline-flex items-center gap-2"
            onClick={() => (window.location.href = "/post-tuition")}
          >
            <i
              className="fa-solid fa-circle-plus text-white"
              style={{ fontSize: 10 }}
              aria-hidden="true"
            ></i>
            <span className="bg-gradient-to-r from-emerald-400 via-emerald-400 to-emerald-400 bg-clip-text text-transparent">
              Post Tuition
            </span>
          </button>
        </div>

        {/* counter section  */}
        {/* <TNCounterSection /> */}

        {/* searchbar */}
        <div className="input-tutor">
          <svg
            className="svg"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M11.5 6C11.3949 6.00006 11.2925 5.96705 11.2073 5.90565C11.1221 5.84425 11.0583 5.75758 11.0251 5.65792L10.7623 4.86908C10.6623 4.57101 10.4288 4.33629 10.13 4.23693L9.34102 3.97354C9.24166 3.94019 9.1553 3.87649 9.09411 3.79142C9.03292 3.70635 9 3.60421 9 3.49943C9 3.39465 9.03292 3.29252 9.09411 3.20745C9.1553 3.12238 9.24166 3.05867 9.34102 3.02532L10.13 2.76193C10.4282 2.66191 10.663 2.42852 10.7623 2.12979L11.0258 1.34094C11.0591 1.24161 11.1229 1.15526 11.2079 1.09409C11.293 1.03291 11.3952 1 11.5 1C11.6048 1 11.707 1.03291 11.7921 1.09409C11.8771 1.15526 11.9409 1.24161 11.9742 1.34094L12.2377 2.12979C12.2868 2.27697 12.3695 2.4107 12.4792 2.52041C12.589 2.63013 12.7227 2.71281 12.87 2.76193L13.659 3.02532C13.7583 3.05867 13.8447 3.12238 13.9059 3.20745C13.9671 3.29252 14 3.39465 14 3.49943C14 3.60421 13.9671 3.70635 13.9059 3.79142C13.8447 3.87649 13.7583 3.94019 13.659 3.97354L12.87 4.23693C12.5718 4.33696 12.337 4.57034 12.2377 4.86908L11.9742 5.65792C11.9411 5.75747 11.8774 5.84406 11.7923 5.90545C11.7072 5.96684 11.6049 5.99992 11.5 6Z"
              fill="currentColor"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6 13C5.85133 13.0001 5.7069 12.9504 5.58969 12.859C5.47247 12.7675 5.38921 12.6395 5.35313 12.4952L5.12388 11.5745C4.91418 10.7391 4.26198 10.0868 3.42674 9.87703L2.50619 9.64774C2.36169 9.61194 2.23333 9.52878 2.14159 9.41151C2.04985 9.29425 2 9.14964 2 9.00075C2 8.85185 2.04985 8.70724 2.14159 8.58998C2.23333 8.47272 2.36169 8.38955 2.50619 8.35376L3.42674 8.12446C4.26198 7.91473 4.91418 7.2624 5.12388 6.427L5.35313 5.50629C5.38892 5.36176 5.47207 5.23338 5.58931 5.14162C5.70655 5.04986 5.85113 5 6 5C6.14887 5 6.29345 5.04986 6.41069 5.14162C6.52793 5.23338 6.61108 5.36176 6.64687 5.50629L6.87612 6.427C6.97865 6.83721 7.19071 7.21184 7.48965 7.51082C7.78858 7.80981 8.16313 8.02192 8.57326 8.12446L9.49381 8.35376C9.63831 8.38955 9.76667 8.47272 9.85841 8.58998C9.95015 8.70724 10 8.85185 10 9.00075C10 9.14964 9.95015 9.29425 9.85841 9.41151C9.76667 9.52878 9.63831 9.61194 9.49381 9.64774L8.57326 9.87703C8.16313 9.97957 7.78858 10.1917 7.48965 10.4907C7.19071 10.7897 6.97865 11.1643 6.87612 11.5745L6.64687 12.4952C6.61079 12.6395 6.52753 12.7675 6.41031 12.859C6.2931 12.9504 6.14867 13.0001 6 13Z"
              fill="currentColor"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M13.5005 23C13.3376 23 13.1791 22.9469 13.049 22.8487C12.9189 22.7505 12.8243 22.6127 12.7795 22.456L11.9665 19.61C11.7915 18.9971 11.4631 18.4389 11.0124 17.9882C10.5616 17.5374 10.0035 17.209 9.39054 17.034L6.54454 16.221C6.38795 16.1761 6.25021 16.0815 6.15216 15.9514C6.05411 15.8214 6.00108 15.6629 6.00108 15.5C6.00108 15.3371 6.05411 15.1786 6.15216 15.0486C6.25021 14.9185 6.38795 14.8239 6.54454 14.779L9.39054 13.966C10.0035 13.791 10.5616 13.4626 11.0124 13.0118C11.4631 12.5611 11.7915 12.0029 11.9665 11.39L12.7795 8.544C12.8244 8.38741 12.919 8.24967 13.0491 8.15162C13.1792 8.05357 13.3376 8.00054 13.5005 8.00054C13.6634 8.00054 13.8219 8.05357 13.952 8.15162C14.0821 8.24967 14.1767 8.38741 14.2215 8.544L15.0345 11.39C15.2096 12.0029 15.538 12.5611 15.9887 13.0118C16.4394 13.4626 16.9976 13.791 17.6105 13.966L20.4565 14.779C20.6131 14.8239 20.7509 14.9185 20.8489 15.0486C20.947 15.1786 21 15.3371 21 15.5C21 15.6629 20.947 15.8214 20.8489 15.9514C20.7509 16.0815 20.6131 16.1761 20.4565 16.221L17.6105 17.034C16.9976 17.209 16.4394 17.5374 15.9887 17.9882C15.538 18.4389 15.2096 18.9971 15.0345 19.61L14.2215 22.456C14.1768 22.6127 14.0822 22.7505 13.9521 22.8487C13.822 22.9469 13.6635 23 13.5005 23Z"
              fill="currentColor"
            />
          </svg>
          <input
            className="search"
            type="text"
            placeholder="সাবজেক্টের নাম দিয়ে খুঁজো..."
            value={searchTerm}
            onChange={(e) => {
              const value = e.target.value;

              // 1) update UI instantly
              setSearchTerm(value);

              // 2) debounce API-triggering filter update
              if (searchDebounceRef.current)
                clearTimeout(searchDebounceRef.current);

              searchDebounceRef.current = setTimeout(() => {
                setFilters((prev) => ({ ...prev, search: value }));
                setCurrentPage(1);
              }, 350);
            }}
          />
        </div>

        {/*  Filter Section */}
        <div className="search-filter-section">
          <div className="search-container">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`filter-toggle ${showFilters ? "active" : ""}`}
            >
              <Filter size={18} />
              <span>ফিল্টার</span>
              {hasActiveFilters && <div className="filter-dot"></div>}
            </button>
          </div>

          {/* Filter Panel */}
          <div className={`filter-panel ${showFilters ? "show" : ""}`}>
            <div className="filter-header">
              <h3>টিউটর ফিলটার করো</h3>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="clear-filters">
                  Clear All
                </button>
              )}
            </div>

            {/* Filter Categories */}
            <div className="filter-grid">
              <div className="filter-group">
                <label>Medium</label>
                <Select
                  options={[
                    { value: "", label: "All Mediums" },
                    ...mapToOptions(filterOptions.mediums),
                  ]}
                  value={
                    filters.medium_id
                      ? {
                          value: filters.medium_id,
                          label: filterOptions.mediums.find(
                            (m) => m.id == filters.medium_id,
                          )?.name,
                        }
                      : { value: "", label: "All Mediums" }
                  }
                  onChange={(selected) =>
                    handleFilterChange("medium_id", selected?.value || "")
                  }
                  isClearable
                />
              </div>

              <div className="filter-group">
                <label>Level</label>
                <Select
                  options={[
                    { value: "", label: "All Levels" },
                    ...mapToOptions(filterOptions.levels),
                  ]}
                  value={
                    filters.level_id
                      ? {
                          value: filters.level_id,
                          label: filterOptions.levels.find(
                            (l) => l.id == filters.level_id,
                          )?.name,
                        }
                      : { value: "", label: "All Levels" }
                  }
                  onChange={(selected) =>
                    handleFilterChange("level_id", selected?.value || "")
                  }
                  isClearable
                />
              </div>

              <div className="filter-group">
                <label>Subject</label>
                <Select
                  options={[
                    { value: "", label: "All Subjects" },
                    ...mapToOptions(filterOptions.subjects),
                  ]}
                  value={
                    filters.subject_id
                      ? {
                          value: filters.subject_id,
                          label: filterOptions.subjects.find(
                            (s) => s.id == filters.subject_id,
                          )?.name,
                        }
                      : { value: "", label: "All Subjects" }
                  }
                  onChange={(selected) =>
                    handleFilterChange("subject_id", selected?.value || "")
                  }
                  isClearable
                />
              </div>

              <div className="filter-group">
                <label>Division</label>
                <Select
                  options={[
                    { value: "", label: "All Divisions" },
                    ...mapToOptions(filterOptions.divisions),
                  ]}
                  value={
                    filters.division_id
                      ? {
                          value: filters.division_id,
                          label: filterOptions.divisions.find(
                            (d) => d.id == filters.division_id,
                          )?.name,
                        }
                      : { value: "", label: "All Divisions" }
                  }
                  onChange={(selected) => {
                    handleFilterChange("division_id", selected?.value || "");
                    handleFilterChange("district_id", "");
                    handleFilterChange("area_id", "");
                  }}
                  isClearable
                />
              </div>

              <div className="filter-group">
                <label>District</label>
                <Select
                  options={[
                    { value: "", label: "All Districts" },
                    ...mapToOptions(
                      filterOptions.districts.filter(
                        (d) =>
                          !filters.division_id ||
                          d.division_id == filters.division_id,
                      ),
                    ),
                  ]}
                  value={
                    filters.district_id
                      ? {
                          value: filters.district_id,
                          label: filterOptions.districts.find(
                            (d) => d.id == filters.district_id,
                          )?.name,
                        }
                      : { value: "", label: "All Districts" }
                  }
                  onChange={(selected) => {
                    handleFilterChange("district_id", selected?.value || "");
                    handleFilterChange("area_id", "");
                  }}
                  isClearable
                />
              </div>

              <div className="filter-group">
                <label>Area</label>
                <Select
                  options={[
                    { value: "", label: "All Areas" },
                    ...mapToOptions(
                      filterOptions.areas.filter(
                        (a) =>
                          !filters.district_id ||
                          a.district_id == filters.district_id,
                      ),
                    ),
                  ]}
                  value={
                    filters.area_id
                      ? {
                          value: filters.area_id,
                          label: filterOptions.areas.find(
                            (a) => a.id == filters.area_id,
                          )?.name,
                        }
                      : { value: "", label: "All Areas" }
                  }
                  onChange={(selected) =>
                    handleFilterChange("area_id", selected?.value || "")
                  }
                  isClearable
                />
              </div>

              <div className="filter-group">
                <label>Gender</label>
                <Select
                  options={[
                    { value: "", label: "Any" },
                    { value: "Male", label: "Male" },
                    { value: "Female", label: "Female" },
                  ]}
                  value={
                    filters.gender
                      ? { value: filters.gender, label: filters.gender }
                      : { value: "", label: "Any" }
                  }
                  onChange={(selected) =>
                    handleFilterChange("gender", selected?.value || "")
                  }
                  isClearable
                />
              </div>

              {/*

 <div className="filter-group">
                <label>Teaching Mode</label>
                <Select
                  options={[
                    { value: "", label: "Any" },
                    { value: "Online", label: "Online" },
                    { value: "In-Person", label: "In-Person" },
                    { value: "Both", label: "Both" },
                  ]}
                  value={
                    filters.mode
                      ? { value: filters.mode, label: filters.mode }
                      : { value: "", label: "Any" }
                  }
                  onChange={(selected) =>
                    handleFilterChange("mode", selected?.value || "")
                  }
                  isClearable
                />
              </div>

               <div className="filter-group">
        <label>Availability</label>
        <Select
          options={[{ value: "", label: "Any Day" }, ...filterOptions.availabilities.map((day) => ({
            value: day,
            label: day
          }))]}
          value={
            filters.availability
              ? { value: filters.availability, label: filters.availability }
              : { value: "", label: "Any Day" }
          }
          onChange={(selected) => handleFilterChange("availability", selected?.value || "")}
          isClearable
        />
      </div> */}
            </div>
          </div>
        </div>

        {/* Results Header */}
        <div className="results-header">
          {/* <div className="results-count">
            <span className="count-number">{loading ? "…" : totalTutors}</span>
            <span className="count-text">tutors found</span>
          </div> */}
          {hasActiveFilters && (
            <div className="active-filters">
              {filters.search && (
                <span className="filter-tag">
                  Search: "{filters.search}"
                  <button
                    onClick={() => {
                      setSearchTerm(""); // optional: clear local display
                      setFilters((prev) => ({ ...prev, search: "" }));
                      setCurrentPage(1); // reset to first page
                    }}
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
              {/* {Object.entries(filters).map(
                ([key, value]) =>
                  value && (
                    <span key={key} className="filter-tag">
                      {key.replace("_", " ")}: {value}
                      <button onClick={() => handleFilterChange(key, "")}>
                        <X size={12} />
                      </button>
                    </span>
                  )
              )} */}
            </div>
          )}
        </div>
        {/* Tutor List */}
        <div className="tutor-list">
          {loading ? (
            <TNSkeletonList count={6} />
          ) : tutors.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon">
                <GraduationCap size={48} />
              </div>
              <h3>No tutors found</h3>
              <p>Try adjusting your search criteria or clearing some filters</p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="btn-primary">
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            tutors.map((tutorData, index) => {
              const tutor = formatTutorData(tutorData);

              return (
                <div
                  className="tutor-card"
                  key={tutor.id}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Collapsible Content */}
                  <div className="tutor-card__content">
                    {/* Profile Image + Rating */}
                    <div className="tutor-image">
                      <div className="avatar">
                        {tutor.profile_picture ? (
                          <img
                            src={tutor.profile_picture || "/default-avatar.png"}
                            onError={(e) => {
                              e.currentTarget.src = "/default-avatar.png";
                            }}
                            alt={tutor.name}
                          />
                        ) : (
                          <div className="avatar-placeholder">
                            {tutor.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tutor Details */}
                    <div className="tutor-info">
                      <div className="tutor-header">
                        <h2 className="tutor-name">{tutor.name}</h2>
                        {tutor.is_verified && (
                          <span
                            className="tnv-badge-chip"
                            title="University Verified"
                          >
                            <BadgeCheck size={20} aria-hidden="true" />
                            <span>University Verified</span>
                          </span>
                        )}

                        {/* <div className="price-tag">
                    <span>Contact for pricing</span>
                    </div> */}
                      </div>

                      {/* Education */}
                      <div className="qualification">
                        <span className="iconBox">
                          <GraduationCap size={30} className="iconWhite" />
                        </span>
                        <span>
                          {tutor.degree} in {tutor.field_of_study} —{" "}
                          {tutor.institute}
                        </span>
                      </div>

                      {/* Study Duration */}
                      {/* {(tutor.start_year || tutor.end_year) && (
                      <div className="study-years">
                   <span>
                          {tutor.start_year || "?"} - {tutor.end_year || "Present"}
                      </span>
                        </div>
                         )} */}

                      {/* Meta Info */}
                      <div className="tutor-meta">
                        {tutor.areas.length > 0 ? (
                          <div className="meta-item location">
                            <span className="iconBox">
                              <MapPinCheck size={30} className="iconWhite" />
                            </span>
                            <span>
                              {tutor.areas
                                .map((area) => area.split(",")[0].trim()) // Area names only
                                .join(" | ")}
                              {" - "}
                              {tutor.areas[0].split(",")[1]?.trim()}{" "}
                              {/* District/Division once */}
                            </span>
                          </div>
                        ) : tutor.location ? (
                          <div className="meta-item location">
                            <span>{tutor.location}</span>
                          </div>
                        ) : null}

                        {/* {tutor.teaching_mode && (
                          <div className="meta-item mode">
                              <span>Mode: {tutor.teaching_mode}</span>
                           </div>
                               )} */}

                        {tutor.gender && (
                          <div className="meta-item gender">
                            <UserRound size={30} className="iconWhite" />
                            <span>{tutor.gender}</span>
                          </div>
                        )}

                        {/* Availability */}
                        {/* {tutor.availability.length > 0 && (
                          <div className="meta-item availability">
                        <Clock size={16} />
                       <span>
                     {tutor.availability.slice(0, 2).map((day, i) => (
                    <span key={i} className="time-slot">
                      {day}
                      {i < Math.min(tutor.availability.length, 2) - 1 ? ", " : ""}
                          </span>
                         ))}
                            {tutor.availability.length > 2 && (
                    <span className="more-times">
                      +{tutor.availability.length - 2} more
                    </span>
                  )}
                </span>
              </div>
            )} */}
                      </div>

                      {/* Bio */}
                      {tutor.bio && (
                        <p className="bio">{sanitizeBio(tutor.bio)}</p>
                      )}

                      {/* Subjects, Mediums, Levels */}
                      <div className="sml">
                        {/* Mediums */}
                        {tutor.mediums.length > 0 && (
                          <div className="subjects">
                            <div className="subject-label">Medium:</div>
                            <div className="indiana-scroll-container--hide-scrollbars">
                              {tutor.mediums.map((medium, i) => (
                                <span key={i} className="subject-tag mode-tag">
                                  {medium}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Subjects */}
                        {tutor.subjects.length > 0 && (
                          <div className="subjects">
                            <div className="subject-label">Subjects:</div>
                            <div className="indiana-scroll-container--hide-scrollbars">
                              {tutor.subjects.map((subject, i) => (
                                <span key={i} className="subject-tag">
                                  {subject}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Levels */}
                        {tutor.levels.length > 0 && (
                          <div className="subjects">
                            <div className="subject-label">Levels:</div>
                            <div className="indiana-scroll-container--hide-scrollbars">
                              {tutor.levels.map((level, i) => (
                                <span key={i} className="subject-tag level-tag">
                                  {level}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Contact Buttons */}
                      <div className="tutor-actions">
                        {/* Contact options are hidden so users can go to tutors profile to see them  */}

                        {/* {tutor.phone_number && (
                          <a
                            href={`tel:${tutor.phone_number}`}
                            className="btn-call"
                          >
                            <Phone size={16} />
                            <span>Call Now</span>
                          </a>
                        )}
                        {tutor.whatsapp_number && (
                          <a
                            href={`https://wa.me/${tutor.whatsapp_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-call whatsapp"
                          >
                            <Phone size={16} />
                            <span>WhatsApp</span>
                          </a>
                        )} */}
                        <a href={`/tutor/${tutor.id}`} className="btn-profile">
                          <span>প্রোফাইল দেখো</span>
                          <ExternalLink size={14} />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Show More / Less Button (outside collapsible content) */}
                  <a href={`/tutor/${tutor.id}`} className="show-more">
                    প্রোফাইল দেখো ➤
                  </a>
                </div>
              );
            })
          )}
        </div>
        {/* Pagination */}
        {Number(totalPages) > 1 && (
          <div className="pagination">
            <button
              onClick={goPrev}
              disabled={Number(currentPage) <= 1}
              className="pagination-btn"
            >
              Previous
            </button>

            {visiblePages.map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`pagination-btn ${
                  Number(currentPage) === page ? "active" : ""
                }`}
                aria-current={Number(currentPage) === page ? "page" : undefined}
              >
                {page}
              </button>
            ))}

            <button
              onClick={goNext}
              disabled={Number(currentPage) >= Number(totalPages)}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default TutorNetwork;
